/**
 * One comanda, across the whole display.
 *
 * A long order never fitted its card, and the card is 460 pixels wide on a
 * screen that does not scroll by touch — reading a table of twelve meant paging
 * a narrow column with two buttons. This is the same comanda with the whole
 * screen to itself.
 *
 * Three things it is careful about:
 *
 * - It holds an identifier, never a copy. Its content is resolved from the live
 *   board data on every render, so a dish added while it is open appears, and a
 *   dish marked ready inside it stays marked (FR-011).
 * - It carries the card's actions. A cook who has to close it to press "pronto"
 *   would stop using it (FR-011a).
 * - When the order is finished or cancelled elsewhere it says so, instead of
 *   presenting a dead ticket as live (FR-012). A kitchen reading a dead ticket
 *   cooks it twice.
 *
 * It renders the same body components the cards render — not a second layout of
 * its own, which would drift.
 */
import { useState } from "react";
import { useTranslation } from "../i18n";
import type { KdsBoardConfig } from "@shared/kds/table-board";
import { isActiveKitchenItem, itemCategoryId } from "@shared/kds/table-board";
import DishDetailModal from "./DishDetailModal";
import ModalShell from "./ModalShell";
import OrderCardActions from "./OrderCardActions";
import OrderCardItems from "./OrderCardItems";
import ScrollPane from "./ScrollPane";
import TableCardBody from "./TableCardBody";
import { orderTitle } from "./OrderCardHeader";
import { useOrderActions } from "../hooks/useOrderActions";
import { useOrderTimers } from "../hooks/useOrderTimers";
import { orderCardFlags } from "../lib/order-card";
import {
  resolveComandaTarget,
  type ComandaSource,
  type ComandaTarget,
} from "../lib/comanda-selectors";

interface ComandaModalProps {
  target: ComandaTarget;
  /** The board's live data. Re-read on every render — never snapshotted. */
  source: ComandaSource;
  config?: KdsBoardConfig;
  now?: number;
  flat?: boolean;
  compact?: boolean;
  onClose: () => void;
}

function GoneNotice({ reason, onClose }: { reason: string; onClose: () => void }) {
  const { t } = useTranslation();
  const message =
    reason === "finished"
      ? t("kds.comanda.goneFinished", "Questa comanda è stata chiusa o annullata: non c'è più niente da preparare.")
      : reason === "empty"
        ? t("kds.comanda.goneEmpty", "Non è rimasto nessun piatto di questa comanda per questa postazione.")
        : t("kds.comanda.goneNotFound", "Questa comanda non è più sul monitor.");
  return (
    <ModalShell title={t("kds.comanda.goneTitle", "Comanda non più attiva")} onClose={onClose}>
      <div className="flex-1 flex items-center justify-center px-10 text-center">
        <p className="text-kds-dish text-kds-muted max-w-2xl">
          {message}
        </p>
      </div>
    </ModalShell>
  );
}

/** One station's share of one order, with the actions its card would offer. */
function StationOrderComanda({
  order,
  items,
  compact,
  onClose,
}: {
  order: any;
  items: any[];
  compact?: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [detailItem, setDetailItem] = useState<any | null>(null);
  const timers = useOrderTimers(order, items);
  const actions = useOrderActions(order);
  const flags = orderCardFlags({ order, items });
  const currentWave = order?.currentWave ?? order?.currentCourse ?? 1;

  return (
    <>
      <ModalShell
        title={orderTitle(order, t)}
        subtitle={
          <span>
            {t("kds.comanda.orderSubtitle", "#{{number}} · {{count}} piatti", {
              number: order?.dailyNumber ?? "",
              count: items.length,
            })}
            {order?.customerName ? ` · ${order.customerName}` : ""}
          </span>
        }
        onClose={onClose}
        footer={
          <OrderCardActions
            compact={compact}
            hasCookingFired={flags.hasCookingFired}
            showFireCourse={!compact && flags.hasUnfiredWave}
            showComplete={!compact && flags.allItemsDone}
            nextWave={currentWave + 1}
            loadingAction={actions.loadingAction}
            onMarkAllReady={() => actions.markAllReady(items)}
            onFireNextWave={actions.fireNextWave}
            onComplete={actions.completeOrder}
          />
        }
      >
        <ScrollPane size="roomy" bodyClassName="px-8 py-6">
          <OrderCardItems
            items={items}
            compact={compact}
            now={timers.now}
            waveFiredAt={timers.waveFiredAt}
            activeWave={timers.activeWave}
            loadingItemIds={actions.loadingItemIds}
            onAdvance={actions.advanceItem}
            onOpenDish={setDetailItem}
          />
          {order?.notes && (
            <div className="mt-6 text-kds-note text-kds-muted bg-kds-card/30 rounded-lg px-5 py-4">
              &#x1F4DD; {order.notes}
            </div>
          )}
        </ScrollPane>
      </ModalShell>

      {detailItem && (
        <DishDetailModal item={detailItem} onClose={() => setDetailItem(null)} />
      )}
    </>
  );
}

export default function ComandaModal({
  target,
  source,
  config,
  now = Date.now(),
  flat,
  compact,
  onClose,
}: ComandaModalProps) {
  const { t } = useTranslation();
  const [showServed, setShowServed] = useState(false);
  const [detailItem, setDetailItem] = useState<any | null>(null);

  const resolved = resolveComandaTarget(target, source);

  if (resolved.kind === "gone") {
    return <GoneNotice reason={resolved.reason} onClose={onClose} />;
  }

  if (resolved.kind === "station-order") {
    return (
      <StationOrderComanda
        order={resolved.order}
        items={resolved.items}
        compact={compact}
        onClose={onClose}
      />
    );
  }

  const { group } = resolved;
  const boardConfig = config as KdsBoardConfig;
  const activeItems = group.items.filter((i: any) => isActiveKitchenItem(i, boardConfig));
  const servedItems = group.items.filter(
    (i: any) =>
      i.status === "SERVED" && boardConfig.categoryToBucket[itemCategoryId(i) ?? ""] != null,
  );

  return (
    <>
      <ModalShell
        title={group.tableName}
        subtitle={
          <span>
            {t("kds.comanda.toPrepare", "{{count}} piatti da preparare", { count: activeItems.length })}
            {servedItems.length > 0 &&
              ` · ${t("kds.comanda.alreadyOut", "{{count}} già usciti", { count: servedItems.length })}`}
          </span>
        }
        onClose={onClose}
        footer={
          servedItems.length > 0 ? (
            <button
              type="button"
              onClick={() => setShowServed((v) => !v)}
              className="inline-flex items-center gap-2 px-6 rounded-lg bg-kds-card text-kds-text text-kds-action min-h-touch hover:bg-kds-card/70 transition-colors"
            >
              {showServed
                ? t("kds.comanda.hideServed", "Nascondi i piatti usciti")
                : t("kds.comanda.showServed", "Mostra i {{count}} già usciti", { count: servedItems.length })}
            </button>
          ) : undefined
        }
      >
        <ScrollPane size="roomy" bodyClassName="px-8 py-6 space-y-5">
          <TableCardBody
            activeItems={activeItems}
            servedItems={servedItems}
            showServed={showServed}
            config={boardConfig}
            now={now}
            flat={flat}
            onOpenDish={setDetailItem}
          />
        </ScrollPane>
      </ModalShell>

      {detailItem && (
        <DishDetailModal item={detailItem} onClose={() => setDetailItem(null)} />
      )}
    </>
  );
}
